import { useEffect, useMemo, useState } from 'react';
import './ProgramPages.css';
import programHero1 from './assets/news/news1.jpg';
import programHero2 from './assets/news/news4.jpg';
import programHero3 from './assets/news/news7.jpg';
import spAssembly from './assets/SPPathwayAssembly.png';
import spCamping from './assets/SPPathwayCamping.png';
import spActivity from './assets/SPPathwayActivity.jpg';
import spCoreValues from './assets/SPPathwayCoreValues.png';
import spPE from './assets/SPPathwayPE.png';
import spLab from './assets/SPPathwayLab.png';
import { EDUCATION_DEFAULTS } from './data/educationDefaults';

const DEFAULT_SPECIAL_PROGRAM_IMAGES = [
  spAssembly,
  spCamping,
  spActivity,
  spCoreValues,
  spPE,
  spLab
];

const getSpecialProgramFallback = (title, idx) => {
  const lower = (title || '').toLowerCase();
  if (lower.includes('bridg')) return spAssembly;
  if (lower.includes('club')) return spCamping;
  if (lower.includes('leader')) return spActivity;
  if (lower.includes('communit') || lower.includes('service')) return spCoreValues;
  if (lower.includes('eca') || lower.includes('extra-curricular')) return spPE;
  if (lower.includes('talent')) return spLab;
  return DEFAULT_SPECIAL_PROGRAM_IMAGES[idx % DEFAULT_SPECIAL_PROGRAM_IMAGES.length] || spAssembly;
};

const getSpecialProgramMeta = (title, idx) => {
  const lower = (title || '').toLowerCase();
  if (lower.includes('bridg')) {
    return {
      category: 'Transition & Language',
      code: '01',
      badge: 'Academic Support',
      benefit: 'Smooth Transition into Cambridge Rigor'
    };
  }
  if (lower.includes('club')) {
    return {
      category: 'Interests & Innovation',
      code: '02',
      badge: 'Creative Discovery',
      benefit: 'Passion Projects & Collaborative Skills'
    };
  }
  if (lower.includes('leader')) {
    return {
      category: 'Character & Service',
      code: '03',
      badge: 'Student Voice',
      benefit: 'Executive Responsibility & Team Leadership'
    };
  }
  if (lower.includes('communit') || lower.includes('service')) {
    return {
      category: 'Global Citizenship',
      code: '04',
      badge: 'Social Impact',
      benefit: 'Empathetic Action & Meaningful Outreach'
    };
  }
  if (lower.includes('eca') || lower.includes('extra-curricular')) {
    return {
      category: 'Wellness & Athletics',
      code: '05',
      badge: 'Physical & Arts',
      benefit: 'Resilience, Team Dynamics & Disciplined Habits'
    };
  }
  if (lower.includes('talent')) {
    return {
      category: 'High Performance',
      code: '06',
      badge: 'Mastery & Coaching',
      benefit: 'Specialized Coaching & Showcase Opportunities'
    };
  }
  const codeNum = String(idx + 1).padStart(2, '0');
  return {
    category: 'Enrichment Pathway',
    code: codeNum,
    badge: 'Student Development',
    benefit: 'Holistic Growth Beyond the Classroom'
  };
};

const API_BASE = process.env.REACT_APP_API_BASE || 'https://permataharapansecondary.sch.id/api';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/i);
  return match ? match[1] : null;
};

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('?')[0].toLowerCase();
  return (
    clean.endsWith('.mp4') ||
    clean.endsWith('.webm') ||
    clean.endsWith('.ogg') ||
    clean.endsWith('.mov') ||
    url.includes('youtube.com') ||
    url.includes('youtu.be') ||
    url.includes('/videos/')
  );
};

const resolveMediaUrl = (mediaId, directUrl, mediaMap, fallback) => {
  if (mediaId && mediaMap && mediaMap[mediaId]) {
    return mediaMap[mediaId];
  }
  if (directUrl && typeof directUrl === 'string') {
    const isFake = /\/uploads\/homepage\/(highlights|media|homepage)\/media_\d+\.jpg$/i.test(directUrl);
    if (!isFake) {
      if (directUrl.startsWith('http') || directUrl.startsWith('blob:') || directUrl.startsWith('data:')) {
        return directUrl;
      }
      if (directUrl.startsWith('/')) {
        const path = (directUrl.startsWith('/api/') || directUrl === '/api') ? directUrl : `/api${directUrl}`;
        return `${API_ORIGIN}${path}`;
      }
    }
  }
  if (mediaId && typeof mediaId === 'string') {
    const isFake = /\/uploads\/homepage\/(highlights|media|homepage)\/media_\d+\.jpg$/i.test(mediaId);
    if (!isFake) {
      if (mediaId.startsWith('http') || mediaId.startsWith('blob:') || mediaId.startsWith('data:')) {
        return mediaId;
      }
      if (mediaId.startsWith('/')) {
        const path = (mediaId.startsWith('/api/') || mediaId === '/api') ? mediaId : `/api${mediaId}`;
        return `${API_ORIGIN}${path}`;
      }
    }
    if (mediaMap && mediaMap[mediaId]) {
      return mediaMap[mediaId];
    }
  }
  return fallback;
};

// Icon Components
const AssessmentIcons = {
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a1.5 1.5 0 0 0-1.5 1.5v12a1.5 1.5 0 0 0 1.5 1.5H17" />
      <path d="M6 12h3v8H6z" />
      <path d="M9 8h3v12H9z" />
      <path d="M12 5h3v15h-3z" />
      <path d="M15 9h3v11h-3z" />
    </svg>
  ),
  clipboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <path d="M9 14h6" />
      <path d="M9 10h6" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  trending: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  book: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  lightbulb: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
      <polygon points="12 2 15.09 10.26 24 10.26 17.55 16.52 19.64 24.78 12 18.52 4.36 24.78 6.45 16.52 0 10.26 8.91 10.26" />
    </svg>
  ),
  award: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
};

const getAssessmentIcon = (iconType) => {
  return AssessmentIcons[iconType] || AssessmentIcons.chart;
};

function EducationProgramPage({ page, onNavigate }) {
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_education');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    const loadMedia = async () => {
      try {
        const [resHome, resEdu] = await Promise.allSettled([
          fetch(`${API_BASE}/homepage/cms/media?type=image`, { credentials: 'omit' }),
          fetch(`${API_BASE}/education/cms/media`, { credentials: 'omit' })
        ]);
        const map = {};
        for (const res of [resHome, resEdu]) {
          if (res.status === 'fulfilled' && res.value && res.value.ok) {
            try {
              const json = await res.value.json();
              const items = Array.isArray(json.data) ? json.data : [];
              items.forEach(m => {
                if (m.id && m.url) {
                  map[m.id] = m.url;
                  map[String(m.id)] = m.url;
                }
              });
            } catch (_) {}
          }
        }
        if (Object.keys(map).length > 0) {
          setMediaMap(prev => ({ ...prev, ...map }));
          try { sessionStorage.setItem('cms_media_education', JSON.stringify(map)); } catch (_) {}
        }
      } catch (err) {
        console.warn('[Education] Media load error:', err);
      }
    };
    loadMedia();
  }, []);

  const FALLBACK_IMAGES = [programHero1, programHero2, programHero3];

  // Hero slides — resolve relative paths and media IDs
  const rawSlides = useMemo(() => {
    if (Array.isArray(page?.hero_slides) && page.hero_slides.length > 0) return page.hero_slides;
    if (Array.isArray(page?.slides) && page.slides.length > 0) return page.slides.map(s => s.image || s.url || s);
    return [programHero1, programHero2, programHero3];
  }, [page?.hero_slides, page?.slides]);

  const heroSlides = useMemo(() => {
    return rawSlides.map((s, idx) => {
      const mediaId = typeof s === 'object' ? (s?.media_id || s?.image_id || s?.image || s?.url || s?.id) : (typeof s === 'string' ? s : null);
      const directUrl = typeof s === 'string' ? s : (s?.url || s?.image_url || s?.image);
      const fallback = FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length];
      const resolved = resolveMediaUrl(mediaId, directUrl, mediaMap, fallback) || fallback;
      const ytId = extractYouTubeId(resolved);
      const isVid = isVideoUrl(resolved) || !!ytId;
      return {
        id: (typeof s === 'object' && s?.id) ? s.id : `hero-slide-${idx}`,
        url: resolved,
        isVideo: isVid,
        isYouTube: !!ytId,
        youtubeId: ytId
      };
    });
  }, [rawSlides, mediaMap]);

  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.edu-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  // ── Hero text ────────────────────────────────────────────────────────────
  const heroTitle   = page?.title   || EDUCATION_DEFAULTS.title;
  const heroSummary = page?.summary || EDUCATION_DEFAULTS.summary;

  // ── Program levels — resolve images ──────────────────────────────────────
  const rawProgramLevels = Array.isArray(page?.program_levels) && page.program_levels.length > 0
    ? page.program_levels
    : EDUCATION_DEFAULTS.program_levels;

  const filteredProgramLevels = rawProgramLevels.filter(lvl => {
    if (!lvl || typeof lvl !== 'object') return false;
    if (lvl.delete !== undefined || lvl.deleted === true || lvl._deleted === true) return false;
    const title = (lvl.title || '').trim().toLowerCase();
    if (!title || title === 'xxx') return false;
    return true;
  });

  const finalLevelsList = filteredProgramLevels.length > 0 ? filteredProgramLevels : EDUCATION_DEFAULTS.program_levels;

  const programLevels = finalLevelsList.map(
    (level, i) => ({
      ...level,
      image: resolveMediaUrl(level.image_id || level.image, level.image_url || level.image, mediaMap, FALLBACK_IMAGES[i % FALLBACK_IMAGES.length]),
    })
  );

  // ── Curriculum overview ──────────────────────────────────────────────────
  const defaultTracks = [
    {
      title: 'Preschool',
      subtitle: 'Preschool',
      description: 'Photo of Kiddy students and their activity',
      highlights: [
        'Play-based learning that builds foundational skills',
        'Social-emotional development and early literacy',
        'Creative exploration and motor skill development',
      ],
      image: FALLBACK_IMAGES[0],
    },
    {
      title: 'Kindergarten',
      subtitle: 'Kindergarten',
      description: 'Photo of KG students and their activity',
      highlights: [
        'Structured play and guided discovery',
        'Phonics, numeracy, and social skills',
        'Transition to formal learning readiness',
      ],
      image: FALLBACK_IMAGES[1],
    },
    {
      title: 'Primary',
      subtitle: 'Primary',
      description: 'Photo of Primary students and their activity',
      highlights: [
        'Strong foundations in literacy and numeracy',
        'Integrated subject learning and project-based activities',
        'Character Shaping Program introduction',
      ],
      image: FALLBACK_IMAGES[2],
    },
    {
      title: 'Secondary',
      subtitle: 'Secondary',
      description: 'Permata Harapan Secondary\'s curriculum consists of three interconnected components — the Intra Curriculum (Kurikulum Intl), the Extra Curriculum (ECA), and the Co-Curriculum (CCA).',
      highlights: [
        'Intra Curriculum serves as the core academic programme, providing students with the foundational knowledge and skills essential to their formal education.',
        'The Co-Curriculum (CCA) complements academic learning by providing structured activities that develop skills, values, and competencies aligned with the curriculum.',
        'The Extra Curriculum (ECA) offers additional opportunities for enrichment, allowing students to explore interests, talents, and personal development beyond formal requirements.',
        'Together, Co-Curriculum and Extra Curriculum support the Students Development Program (SDP), which serves as the platform for developing well-rounded students.',
      ],
      image: FALLBACK_IMAGES[0],
    },
  ];

  const dbCurriculum = page?.curriculum_overview || EDUCATION_DEFAULTS.curriculum_overview;
  const DEFAULT_PILLARS = [
    {
      title: 'NATIONAL FOUNDATIONS',
      description: 'Strong alignment with local standards, values education, and Bahasa Indonesia literacy.',
    },
    {
      title: 'CAMBRIDGE PERSPECTIVE',
      description: 'Inquiry, language mastery, and global awareness woven across core subjects.',
    },
    {
      title: 'HOLISTIC GROWTH (SDP)',
      description: 'Co- and extra-curricular programs nurture leadership, service, and life skills.',
    },
  ];

  const rawPillars = Array.isArray(dbCurriculum?.pillars) && dbCurriculum.pillars.length > 0
    ? dbCurriculum.pillars
    : DEFAULT_PILLARS;

  const validPillars = rawPillars.filter(p => {
    if (!p || typeof p !== 'object') return false;
    if (p.delete !== undefined || p.deleted === true || p._deleted === true) return false;
    const title = (p.title || '').trim().toLowerCase();
    if (!title || title === 'xxx') return false;
    return true;
  });

  const rawTracks = Array.isArray(dbCurriculum?.tracks) && dbCurriculum.tracks.length > 0
    ? dbCurriculum.tracks
    : defaultTracks;

  const validTracks = rawTracks.filter(t => {
    if (!t || typeof t !== 'object') return false;
    if (t.delete !== undefined || t.deleted === true || t._deleted === true) return false;
    const title = (t.title || '').trim().toLowerCase();
    if (!title || title === 'xxx') return false;
    return true;
  });

  const curriculumOverview = {
    kicker: dbCurriculum?.kicker || page?.curriculum_kicker || 'Curriculum Overview',
    title: dbCurriculum?.title || 'National & International Curriculum Pathway',
    summary: dbCurriculum?.summary || 'We blend the national curriculum with Cambridge perspectives so every student grows in faith, character, and academic readiness from Preschool to Secondary.',
    pillars: validPillars.length > 0 ? validPillars : DEFAULT_PILLARS,
    tracks: (validTracks.length > 0 ? validTracks : defaultTracks).map((track, i) => ({
      ...track,
      image: resolveMediaUrl(track.image_id || track.image, track.image_url || track.image, mediaMap, FALLBACK_IMAGES[i % FALLBACK_IMAGES.length]),
    })),
    note: dbCurriculum?.note || 'Each level is designed to build upon previous learning while preparing students for the next stage of their educational journey.',
  };

  // ── Subjects offered — resolve images ─────────────────────────────────────
  const rawSubjects = (page?.subjects_offered || page?.subjects || EDUCATION_DEFAULTS.subjects_offered).filter(s => {
    if (!s) return false;
    if (typeof s === 'object') {
      if (s.delete !== undefined || s.deleted === true || s._deleted === true) return false;
      const name = (s.name || s.title || '').trim().toLowerCase();
      if (!name || name === 'xxx') return false;
    }
    return true;
  });
  const DEFAULT_SUBJECT_POINTS = [
    'Concept mastery with clear learning targets',
    'Practice, feedback, and reflection cycles',
    'Projects that connect to real contexts',
  ];

  const normalizedSubjects = useMemo(
    () =>
      rawSubjects.map((subject, index) => {
        if (typeof subject === 'string') {
          return {
            name: subject,
            code: '',
            description: 'A focused subject pathway that builds understanding, skills, and curiosity.',
            points: DEFAULT_SUBJECT_POINTS,
            image: FALLBACK_IMAGES[index % FALLBACK_IMAGES.length],
          };
        }
        const hasPoints = Array.isArray(subject.points) && subject.points.length > 0;
        return {
          ...subject,
          name: subject.name || `Subject ${index + 1}`,
          code: subject.code || '',
          description: subject.description || (hasPoints ? subject.points.join(' • ') : 'A focused subject pathway that builds understanding, skills, and curiosity.'),
          points: hasPoints ? subject.points : DEFAULT_SUBJECT_POINTS,
          image: resolveMediaUrl(subject.image_id || subject.image, subject.image_url || subject.image, mediaMap, FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawSubjects, mediaMap]
  );

  const [activeSubject, setActiveSubject] = useState(normalizedSubjects[0]);

  // Keep activeSubject in sync whenever normalizedSubjects updates
  useEffect(() => {
    if (normalizedSubjects.length > 0) {
      setActiveSubject(prev => {
        if (!prev) return normalizedSubjects[0];
        const match = normalizedSubjects.find(s => s.name === prev.name);
        return match || normalizedSubjects[0];
      });
    }
  }, [normalizedSubjects]);

  // ── Learning approach ─────────────────────────────────────────────────────
  const dbLearning = page?.learning_approach || EDUCATION_DEFAULTS.learning_approach;
  const learningApproach = {
    kicker: page?.learning_kicker || dbLearning?.kicker || 'Learning Approach',
    title: page?.learning_title || dbLearning?.title || 'Learning Approach & 5E Instructional Model',
    subtitle: page?.learning_subtitle || dbLearning?.subtitle || 'A comprehensive framework that develops curiosity, collaboration, and mastery across every level.',
    description: page?.learning_description || dbLearning?.description || '',
    pedagogicalModel: dbLearning?.pedagogicalModel || dbLearning?.pedagogical_model || {
      title: '5E Instructional Model',
      subtitle: 'Inquiry-based, project-based, or blended learning Pedagogical Model',
      description: 'Sekolah Permata Harapan Secondary adopts the 5E Instructional Model as a school-wide approach:',
      phases: [
        {
          phase: 'Engage',
          icon: 'lightbulb',
          description: 'Activate curiosity and prior knowledge',
          details: 'Start with real-world scenarios or phenomena that spark student interest and connect to prior experiences.',
        },
        {
          phase: 'Explore',
          icon: 'zap',
          description: 'Provide hands-on inquiry-based activities',
          details: 'Students engage in investigations, experiments, and discovery to develop deeper understanding.',
        },
        {
          phase: 'Explain',
          icon: 'book',
          description: 'Facilitate deep conceptual understanding',
          details: 'Guide students to articulate findings, connect to concepts, and develop formal understanding.',
        },
        {
          phase: 'Elaborate',
          icon: 'users',
          description: 'Extend learning through real-world application',
          details: 'Apply knowledge in new contexts, solve problems, and deepen mastery through projects and challenges.',
        },
        {
          phase: 'Evaluate',
          icon: 'target',
          description: 'Reflect, assess, and provide feedback',
          details: 'Students self-assess, receive feedback, and reflect on their learning progress and growth.',
        },
      ],
    },
    signatureStrategies: ((dbLearning?.signatureStrategies && dbLearning.signatureStrategies.length > 0)
      ? dbLearning.signatureStrategies
      : (dbLearning?.signature_strategies && dbLearning.signature_strategies.length > 0)
      ? dbLearning.signature_strategies
      : null)
      ? ((dbLearning?.signatureStrategies && dbLearning.signatureStrategies.length > 0) ? dbLearning.signatureStrategies : dbLearning.signature_strategies).map((strat, idx) => ({
          ...strat,
          image: resolveMediaUrl(strat.image_id || strat.image, strat.image_url || strat.image, mediaMap, FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length]),
        }))
      : [
        {
          title: 'Inquiry-Based Learning (IBL)',
          description: 'Students ask questions, investigate phenomena, and construct understanding through discovery.',
          icon: 'lightbulb',
          outcomes: [
            'Deep conceptual understanding',
            'Critical thinking and problem-solving',
            'Student agency and ownership of learning',
          ],
          image: FALLBACK_IMAGES[0],
        },
        {
          title: 'Project-Based Learning (PBL)',
          description: 'Extended projects that integrate multiple subjects and require collaboration, creativity, and real-world application.',
          icon: 'chart',
          outcomes: [
            'Interdisciplinary connections',
            'Collaboration and communication skills',
            'Authentic learning experiences',
          ],
          image: FALLBACK_IMAGES[1],
        },
        {
          title: 'Problem-Based Learning (PrBL)',
          description: 'Students solve complex, real-world problems that require application of knowledge and skills.',
          icon: 'target',
          outcomes: [
            'Application of learning to real contexts',
            'Resilience and persistence',
            'Innovation and creative solutions',
          ],
          image: FALLBACK_IMAGES[2],
        },
      ],
    integration: dbLearning?.integration || 'These strategies are integrated across all subjects, especially in Humanities, Science, and Global Perspectives, ensuring that students develop not just knowledge, but critical thinking, collaboration, and lifelong learning skills.',
  };

  // ── Assessment system ─────────────────────────────────────────────────────
  const rawAssessment = page?.assessment_system || EDUCATION_DEFAULTS.assessment_system;
  const assessmentSystem = {
    kicker: page?.assessment_kicker || rawAssessment?.kicker || 'Assessment System',
    title: page?.assessment_title || rawAssessment?.title || "How students' progress is evaluated",
    summary: page?.assessment_summary || rawAssessment?.summary || 'We use a balanced assessment system that values daily growth, authentic performance, and mastery of standards.',
    balanced_approach_title: rawAssessment?.balanced_approach_title || 'Our Balanced Approach',
    balanced_approach_description: rawAssessment?.balanced_approach_description || "Assessment at Permata Harapan goes beyond traditional testing. We combine multiple methods to capture the full picture of each student's learning journey—from daily growth to mastery of standards.",
    assessment_methodology_title: rawAssessment?.assessment_methodology_title || 'Our Assessment Methodology',
    assessment_methodology_subtitle: rawAssessment?.assessment_methodology_subtitle || 'Four pillars that guide how we evaluate and support student learning',
    assessment_calendar_header_title: rawAssessment?.assessment_calendar_header_title || 'Assessment Calendar',
    assessment_calendar_header_subtitle: rawAssessment?.assessment_calendar_header_subtitle || 'Key assessment touchpoints throughout the academic year',
    points: (rawAssessment?.points && rawAssessment.points.length > 0)
      ? rawAssessment.points.map((pt, idx) => ({
          ...pt,
          iconType: pt.iconType || pt.icon_type || (idx === 0 ? 'chart' : idx === 1 ? 'zap' : idx === 2 ? 'book' : 'target'),
          details: Array.isArray(pt.details) ? pt.details : [],
        }))
      : (EDUCATION_DEFAULTS.assessment_system?.points || []).map((pt, idx) => ({
          ...pt,
          iconType: pt.iconType || pt.icon_type || (idx === 0 ? 'chart' : idx === 1 ? 'zap' : idx === 2 ? 'book' : 'target'),
          details: Array.isArray(pt.details) ? pt.details : [],
        })),
    methodology: (rawAssessment?.methodology && rawAssessment.methodology.length > 0)
      ? rawAssessment.methodology
      : (EDUCATION_DEFAULTS.assessment_system?.methodology || []),
    what_this_means: (rawAssessment?.what_this_means && rawAssessment.what_this_means.length > 0)
      ? rawAssessment.what_this_means
      : [
          {
            title: 'Personalized Learning',
            description: 'Each student receives targeted support based on their unique strengths and needs.',
            icon: 'book',
          },
          {
            title: 'Clear Progress Tracking',
            description: 'Regular data helps us identify growth and celebrate achievements along the way.',
            icon: 'chart',
          },
          {
            title: 'Open Communication',
            description: 'Parents stay informed with detailed reports and regular conferences throughout the year.',
            icon: 'users',
          },
          {
            title: 'Growth Mindset Focus',
            description: 'Feedback emphasizes effort and improvement, building lasting confidence and resilience.',
            icon: 'zap',
          },
        ],
    assessment_calendar: (rawAssessment?.assessment_calendar && rawAssessment.assessment_calendar.length > 0)
      ? rawAssessment.assessment_calendar
      : [
          {
            period: 'Aug - Sep',
            title: 'Diagnostic Baselines',
            description: "Initial assessments to understand each student's starting point and learning profile.",
            icon: 'calendar',
          },
          {
            period: 'Sep - Nov',
            title: 'Formative Checks & Projects',
            description: 'Weekly observations, exit tickets, and first performance tasks.',
            icon: 'clipboard',
          },
          {
            period: 'Nov - Dec',
            title: 'Mid-Term Assessment',
            description: 'Comprehensive evaluation with parent-teacher conferences and progress reports.',
            icon: 'chart',
          },
          {
            period: 'Jan - Apr',
            title: 'Continued Monitoring',
            description: 'Ongoing formative assessments, project milestones, and intervention adjustments.',
            icon: 'trending',
          },
          {
            period: 'May - Jun',
            title: 'End-Term Assessment',
            description: 'Final summative assessments, portfolio reviews, and year-end certification.',
            icon: 'award',
          },
        ],
  };

  // ── Special programs — resolve images ─────────────────────────────────────
  const specialPrograms = (page?.special_programs || page?.programs || EDUCATION_DEFAULTS.special_programs).map(
    (item, i) => ({
      ...item,
      image: resolveMediaUrl(item.image_id || item.image, item.image_url || item.image, mediaMap, getSpecialProgramFallback(item.title, i)),
    })
  );

  const specialProgramsShell = useMemo(() => {
    const raw = page?.special_programs_shell || {};
    const rawStats = Array.isArray(raw.stats) && raw.stats.length > 0 
      ? raw.stats 
      : [
          { label: 'Program Pathways', value: String(specialPrograms.length) },
          { label: 'Student Activities', value: 'Weekly' },
          { label: 'Participation Options', value: 'All Levels' }
        ];

    const secondaryStats = rawStats.filter(s => (s.label || '').trim().toLowerCase() !== 'program pathways');
    const computedStats = [
      { label: 'Program Pathways', value: String(specialPrograms.length) },
      ...(secondaryStats.length > 0 
        ? secondaryStats 
        : [
            { label: 'Student Activities', value: 'Weekly' },
            { label: 'Participation Options', value: 'All Levels' }
          ])
    ];

    return {
      kicker: raw.kicker || page?.special_programs_kicker || 'HOLISTIC DEVELOPMENT & ENRICHMENT',
      title: raw.title || page?.special_programs_title || 'Special Programs & Enrichment Pathways',
      subtitle: raw.subtitle || page?.special_programs_subtitle || 'Nurturing talents, leadership, and real-world impact beyond the classroom',
      description: raw.description || page?.special_programs_description || 'Our special programs empower students to discover their unique strengths through bridging support, leadership initiatives, competitive talent coaching, and community service.',
      note: raw.note || page?.special_programs_note || 'Co-curricular pathways run in synergy with the Cambridge International Curriculum.',
      stats: computedStats
    };
  }, [page, specialPrograms.length]);

  useEffect(() => {
    if (!normalizedSubjects.length) {
      return;
    }

    if (!activeSubject) {
      setActiveSubject(normalizedSubjects[0]);
      return;
    }

    const stillExists = normalizedSubjects.some(
      (subject) => subject.name === activeSubject.name
    );

    if (!stillExists) {
      setActiveSubject(normalizedSubjects[0]);
    }
  }, [normalizedSubjects, activeSubject]);

  return (
    <section className="education-program-page">
      <section className="education-hero" id="education-overview">
        <div className="education-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeSlide;
              return (
                <div
                  key={slide.id || index}
                  className={`education-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="education-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="Hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="education-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle || 'Education Program'}
                      className="education-hero-img-element"
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="education-hero-slide active education-hero-gradient-fallback" />
          )}
        </div>

        <div className="education-hero-overlay" aria-hidden="true" />

        <div className="education-hero-content-wrap">
          <div className="education-hero-content">
            <p className="education-hero-kicker">
              {page?.kicker || 'Education Program'}
            </p>

            <h1 className="education-hero-title">
              {heroTitle}
            </h1>

            {heroSummary && (
              <p className="education-hero-summary">
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="education-hero-dots" role="tablist" aria-label="Education program hero slides">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`education-hero-dot ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeSlide}
                  >
                    <span className="education-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Education Levels & Academic Stages Section - Redesigned Light Prestige Showcase */}
      <section className="education-levels" id="program-levels">
        <div className="education-levels-container">
          {/* Centered Section Header */}
          <div className="education-levels-header">
            <div className="education-levels-kicker-wrap">
              <span className="education-levels-kicker-badge">
                <span className="education-levels-kicker-dot" />
                <span>{page?.levels_kicker || page?.program_levels_kicker || 'Academic Pathways'}</span>
              </span>
            </div>
            <h2 className="education-levels-title">
              {page?.levels_title || page?.program_levels_title || 'Preschool, Kindergarten, Primary, and Secondary'}
            </h2>
            <div className="education-levels-title-accent" aria-hidden="true" />
            <p className="education-levels-subtitle">
              {page?.levels_subtitle || page?.program_levels_subtitle || page?.levels_description || 'Each stage is thoughtfully designed to meet students at their level while growing faith, skills, and confidence.'}
            </p>
          </div>

          {/* Prestige 4-Column / Responsive Program Stages Grid */}
          <div className="education-levels-grid">
            {programLevels.map((level, idx) => {
              const handleClick = () => {
                const link = (level.link_url || '').trim();
                const ext = (level.external_url || '').trim();
                const slug = (level.slug || '').trim();

                let effectiveTarget = link;
                if (!effectiveTarget) {
                  effectiveTarget = ext || slug;
                }

                if (!effectiveTarget) {
                  if (onNavigate && level.anchor) {
                    onNavigate('education-program', level.anchor);
                  }
                  return;
                }

                if (effectiveTarget.startsWith('http://') || effectiveTarget.startsWith('https://')) {
                  window.open(effectiveTarget, '_blank');
                } else if (effectiveTarget.startsWith('#')) {
                  const targetId = effectiveTarget.substring(1);
                  const el = document.getElementById(targetId);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  } else if (onNavigate) {
                    onNavigate('education-program', targetId);
                  }
                } else if (onNavigate) {
                  const clean = effectiveTarget.replace(/^\//, '');
                  if (clean.includes('#')) {
                    const [s, a] = clean.split('#');
                    onNavigate(s, a);
                  } else {
                    onNavigate(clean, level.anchor);
                  }
                } else {
                  window.location.href = effectiveTarget.startsWith('/') ? effectiveTarget : `/${effectiveTarget}`;
                }
              };
              
              const stageNum = `0${idx + 1}`;

              return (
                <div
                  className="education-level-card edu-animate"
                  key={level.title || idx}
                  id={level.anchor}
                  onClick={handleClick}
                  tabIndex={0}
                  role="button"
                  aria-label={`Explore ${level.title} Program details`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClick();
                    }
                  }}
                >
                  {/* Top Prestige Accent Ribbon */}
                  <div className="education-level-card-ribbon" />

                  {/* Media Frame with Stage Badges */}
                  <div className="education-level-media">
                    <img
                      src={level.image}
                      alt={level.title}
                      className="education-level-img"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length];
                      }}
                    />
                    <div className="education-level-media-overlay" aria-hidden="true" />
                    <span className="education-level-stage-pill">
                      Stage {stageNum}
                    </span>
                    {level.subtitle && (
                      <span className="education-level-age-pill">
                        {level.subtitle}
                      </span>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="education-level-body">
                    <div className="education-level-meta">
                      <span className="education-level-kicker-tag">
                        {level.subtitle || `Stage ${stageNum}`}
                      </span>
                    </div>

                    <h3 className="education-level-title">{level.title}</h3>

                    <div className="education-level-card-divider" aria-hidden="true" />

                    <p className="education-level-description">{level.description}</p>

                    <div className="education-level-card-footer">
                      <span className="education-level-link">
                        <span>Explore Program</span>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="curriculum-overview" id="curriculum-overview">
        <div className="curriculum-overview-container">
          {/* Centered Section Header */}
          <div className="curriculum-overview-header">
            <div className="curriculum-overview-kicker-wrap">
              <span className="curriculum-overview-kicker-badge">
                <span className="curriculum-overview-kicker-dot" aria-hidden="true" />
                {curriculumOverview.kicker || 'Curriculum Overview'}
              </span>
            </div>
            <h3 className="curriculum-overview-title">{curriculumOverview.title}</h3>
            <p className="curriculum-overview-subtitle">{curriculumOverview.summary}</p>
          </div>

          {/* Pillars Cards (National, Cambridge, Holistic) */}
          {curriculumOverview.pillars && curriculumOverview.pillars.length > 0 && (
            <div className="curriculum-overview-pillars">
              {curriculumOverview.pillars.map((pillar, idx) => (
                <div className="curriculum-pillar edu-animate" key={pillar.title || idx}>
                  <div className="curriculum-pillar-top">
                    <span className="curriculum-pillar-number">0{idx + 1}</span>
                    <span className="curriculum-pillar-spark" aria-hidden="true" />
                  </div>
                  <p className="curriculum-pillar-title">{pillar.title}</p>
                  <p className="curriculum-pillar-description">{pillar.description}</p>
                </div>
              ))}
            </div>
          )}

          {/* Tracks Showcase (Preschool, Kindergarten, Primary, Secondary) */}
          <div className="curriculum-overview-grid">
            {curriculumOverview.tracks.map((item, index) => {
              const stageNum = String(index + 1).padStart(2, '0');
              return (
                <article
                  className="curriculum-card edu-animate"
                  key={item.title || index}
                  style={{ animationDelay: `${index * 120}ms` }}
                >
                  <div className="curriculum-card-body">
                    <div className="curriculum-card-top">
                      <span className="curriculum-card-stage-pill">Stage {stageNum}</span>
                      {item.subtitle && item.subtitle !== item.title && (
                        <span className="curriculum-card-kicker">{item.subtitle}</span>
                      )}
                    </div>
                    <h4 className="curriculum-card-heading">{item.title}</h4>
                    <p className="curriculum-card-description">{item.description}</p>

                    {item.highlights && item.highlights.length > 0 && (
                      <ul className="curriculum-card-list">
                        {item.highlights.map((highlight, hIdx) => (
                          <li key={hIdx}>
                            <span className="curriculum-card-bullet" aria-hidden="true">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {item.components && item.components.length > 0 && (
                      <div className="curriculum-components">
                        <p className="curriculum-components-title">Curriculum Structure</p>
                        <div className="curriculum-components-grid">
                          {item.components.map((component, cIdx) => (
                            <span className="curriculum-component" key={cIdx}>
                              {component}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="curriculum-card-media">
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
                      }}
                    />
                    <div className="curriculum-card-media-overlay" aria-hidden="true" />
                    <span className="curriculum-card-media-badge">
                      Stage {stageNum} • {item.title}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Pathways Continuum Advisory Note */}
          {curriculumOverview.note && (
            <div className="curriculum-overview-note edu-animate">
              <div className="curriculum-note-icon-wrap" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </div>
              <div className="curriculum-note-text">
                <span className="curriculum-note-label">Pathways Continuum</span>
                <p>{curriculumOverview.note}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="subjects-offered" id="subjects-offered">
        <div className="subjects-offered-container">
          {/* Centered Header */}
          <div className="subjects-offered-header">
            <div className="subjects-offered-kicker-wrap">
              <span className="subjects-offered-kicker-badge">
                <span className="subjects-offered-kicker-dot" aria-hidden="true" />
                {page?.subjects_kicker || 'Subjects Offered'}
              </span>
            </div>
            <h3 className="subjects-offered-title">
              {page?.subjects_title || 'Balanced Academics, Creativity, and Character Growth'}
            </h3>
            <p className="subjects-offered-subtitle">
              {page?.subjects_subtitle || page?.subjects_description || 'A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.'}
            </p>
          </div>

          {/* Stats Bar */}
          <div className="subjects-offered-stats edu-animate">
            <div className="subjects-stat">
              <span className="subjects-stat-value">{normalizedSubjects.length}</span>
              <span className="subjects-stat-label">Core Subjects</span>
            </div>
            <div className="subjects-stat">
              <span className="subjects-stat-value">{page?.subjects_stats?.[1]?.value || '3'}</span>
              <span className="subjects-stat-label">{page?.subjects_stats?.[1]?.label || 'Learning Domains'}</span>
            </div>
            <div className="subjects-stat">
              <span className="subjects-stat-value">{page?.subjects_stats?.[2]?.value || '100%'}</span>
              <span className="subjects-stat-label">{page?.subjects_stats?.[2]?.label || 'Cambridge + Local Alignment'}</span>
            </div>
          </div>

          {/* Master-Detail Layout */}
          <div className="subjects-offered-layout">
            {/* Subjects Selection List */}
            <div className="subjects-offered-list edu-animate" role="tablist" aria-label="Subjects Offered">
              {normalizedSubjects.map((subject, idx) => {
                const isActive = activeSubject?.name === subject.name;
                const subjectNum = String(idx + 1).padStart(2, '0');
                return (
                  <button
                    key={subject.name || idx}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`subject-card ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveSubject(subject)}
                  >
                    <div className="subject-card-left">
                      <span className="subject-card-number">{subjectNum}</span>
                      <div className="subject-card-info">
                        <div className="subject-card-title-row">
                          <h4 className="subject-card-name">{subject.name}</h4>
                          {subject.code && (
                            <span className="subject-card-code-badge">{subject.code}</span>
                          )}
                        </div>
                        <p className="subject-card-desc">{subject.description}</p>
                      </div>
                    </div>
                    <span className="subject-card-arrow" aria-hidden="true">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Featured Subject Spotlight */}
            {activeSubject && (
              <div className="subject-feature edu-animate" role="tabpanel">
                <div className="subject-feature-header">
                  <div>
                    <span className="subject-feature-kicker">Featured Focus</span>
                    <h4 className="subject-feature-title">{activeSubject.name}</h4>
                  </div>
                  <span className="subject-feature-tag">
                    {activeSubject.code ? activeSubject.code : 'Balanced Pathway'}
                  </span>
                </div>

                <div className="subject-feature-media">
                  <img
                    src={activeSubject.image}
                    alt={activeSubject.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGES[0];
                    }}
                  />
                  <div className="subject-feature-media-overlay" aria-hidden="true" />
                  <span className="subject-feature-media-badge">
                    {activeSubject.code ? `${activeSubject.code} • ` : ''}{activeSubject.name}
                  </span>
                </div>

                <p className="subject-feature-description">{activeSubject.description}</p>

                <div className="subject-feature-divider" aria-hidden="true" />

                <ul className="subject-feature-list">
                  {(activeSubject.points && activeSubject.points.length > 0
                    ? activeSubject.points
                    : DEFAULT_SUBJECT_POINTS
                  ).map((item, i) => (
                    <li key={i}>
                      <span className="subject-feature-bullet" aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="learning-approach" id="learning-approach">
        <div className="learning-approach-container">
          <div className="learning-approach-header">
            <div className="learning-approach-kicker-wrap">
              <span className="learning-approach-kicker-badge">
                <span className="learning-approach-kicker-dot" aria-hidden="true" />
                {learningApproach.kicker || 'Learning Approach'}
              </span>
            </div>
            <h3 className="learning-approach-title">
              {learningApproach.title || '5E Model, Inquiry, Project & Problem-Based Learning'}
            </h3>
            <p className="learning-approach-subtitle">
              {learningApproach.subtitle || 'A comprehensive framework that develops curiosity, collaboration, and mastery across every level.'}
            </p>
          </div>

          {/* 5E Instructional Model */}
          {learningApproach.pedagogicalModel && (
            <div className="five-e-model edu-animate">
              <div className="five-e-header">
                <div className="five-e-badge-wrap">
                  <span className="five-e-badge">Pedagogical Framework</span>
                </div>
                <h4 className="five-e-title">{learningApproach.pedagogicalModel.title}</h4>
                {learningApproach.pedagogicalModel.subtitle && (
                  <p className="five-e-subtitle">{learningApproach.pedagogicalModel.subtitle}</p>
                )}
                {learningApproach.pedagogicalModel.description && (
                  <p className="five-e-description">{learningApproach.pedagogicalModel.description}</p>
                )}
              </div>

              {Array.isArray(learningApproach.pedagogicalModel.phases) && learningApproach.pedagogicalModel.phases.length > 0 && (
                <div className="five-e-phases">
                  {learningApproach.pedagogicalModel.phases.map((phase, index) => {
                    const stepNum = String(index + 1).padStart(2, '0');
                    return (
                      <div
                        className="five-e-phase"
                        key={phase.phase || index}
                        style={{ animationDelay: `${index * 90}ms` }}
                      >
                        <div className="phase-step-indicator">
                          <span className="phase-number">{stepNum}</span>
                        </div>
                        <div className="phase-icon-wrapper">
                          <div className="phase-icon">
                            {getAssessmentIcon(phase.icon || (index === 0 ? 'lightbulb' : index === 1 ? 'zap' : index === 2 ? 'book' : index === 3 ? 'users' : 'target'))}
                          </div>
                        </div>
                        <h5 className="phase-name">{phase.phase}</h5>
                        {phase.description && <p className="phase-title">{phase.description}</p>}
                        {phase.details && <p className="phase-details">{phase.details}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Signature Strategies */}
          {Array.isArray(learningApproach.signatureStrategies) && learningApproach.signatureStrategies.length > 0 && (
            <div className="signature-strategies edu-animate">
              <div className="strategies-header">
                <div className="strategies-badge-wrap">
                  <span className="strategies-badge">Active Learning Methodologies</span>
                </div>
                <h4 className="strategies-title">Signature Learning Strategies</h4>
                <p className="strategies-subtitle">
                  Integrated across all subjects, especially in Humanities, Science, and Global Perspectives
                </p>
              </div>
              <div className="strategies-grid">
                {learningApproach.signatureStrategies.map((strategy, index) => {
                  const acronymMatch = strategy.title ? strategy.title.match(/\(([^)]+)\)/) : null;
                  const acronym = acronymMatch ? acronymMatch[1] : `Method ${index + 1}`;
                  return (
                    <article
                      className="strategy-card edu-animate"
                      key={strategy.title || index}
                      style={{ animationDelay: `${index * 120}ms` }}
                    >
                      <div className="strategy-media">
                        <img
                          src={strategy.image}
                          alt={strategy.title}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];
                          }}
                        />
                        <div className="strategy-overlay" aria-hidden="true" />
                        <span className="strategy-acronym-badge">{acronym}</span>
                        <div className="strategy-icon-badge" aria-hidden="true">
                          {getAssessmentIcon(strategy.icon || (index === 0 ? 'lightbulb' : index === 1 ? 'chart' : 'target'))}
                        </div>
                      </div>
                      <div className="strategy-body">
                        <h5 className="strategy-title">{strategy.title}</h5>
                        <p className="strategy-description">{strategy.description}</p>
                        
                        {Array.isArray(strategy.outcomes) && strategy.outcomes.length > 0 && (
                          <div className="strategy-outcomes-wrap">
                            <span className="strategy-outcomes-label">Key Learning Outcomes:</span>
                            <ul className="strategy-outcomes">
                              {strategy.outcomes.map((outcome, oIdx) => (
                                <li key={oIdx} className="strategy-outcome-item">
                                  <span className="strategy-outcome-bullet" aria-hidden="true">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </span>
                                  <span className="strategy-outcome-text">{outcome}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}

          {/* Integration Note */}
          {learningApproach.integration && (
            <div className="integration-note edu-animate">
              <div className="integration-icon-wrap" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <div className="note-content">
                <div className="integration-badge-row">
                  <span className="integration-badge">Holistic Pedagogy</span>
                </div>
                <h4 className="integration-title">Integrated Learning Across Subjects</h4>
                <p className="integration-text">{learningApproach.integration}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="assessment-system" id="assessment-system">
        <div className="assessment-system-container">
          {/* Header */}
          <div className="assessment-system-header">
            <div className="assessment-system-kicker-wrap">
              <span className="assessment-system-kicker-badge">
                <span className="assessment-system-kicker-dot" aria-hidden="true" />
                {assessmentSystem.kicker || 'Assessment System'}
              </span>
            </div>
            <h3 className="assessment-system-title">{assessmentSystem.title}</h3>
            <p className="assessment-system-subtitle">{assessmentSystem.summary}</p>
          </div>

          {/* Balanced Approach Banner */}
          <div className="assessment-system-intro edu-animate">
            <div className="assessment-intro-icon-wrap" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="m4.93 4.93 4.24 4.24" />
                <path d="m14.83 9.17 4.24-4.24" />
                <path d="m14.83 14.83 4.24 4.24" />
                <path d="m9.17 14.83-4.24 4.24" />
                <circle cx="12" cy="12" r="4" />
              </svg>
            </div>
            <div className="assessment-intro-content">
              <div className="assessment-intro-badge-row">
                <span className="assessment-intro-badge">Evaluation Philosophy</span>
              </div>
              <h4 className="assessment-intro-title">{assessmentSystem.balanced_approach_title}</h4>
              <p className="assessment-intro-desc">{assessmentSystem.balanced_approach_description}</p>
            </div>
          </div>

          {/* Assessment Points (Pillars) */}
          <div className="assessment-system-grid">
            {assessmentSystem.points.map((item, index) => {
              const stepNum = String(index + 1).padStart(2, '0');
              return (
                <article
                  className="assessment-system-card edu-animate"
                  key={item.title || index}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="assessment-card-top">
                    <div className="assessment-card-icon">
                      {getAssessmentIcon(item.iconType)}
                    </div>
                    <span className="assessment-card-step">Stage {stepNum}</span>
                  </div>
                  <div className="assessment-card-body">
                    <h4 className="assessment-card-title">{item.title}</h4>
                    <p className="assessment-card-description">{item.description}</p>
                    
                    {Array.isArray(item.details) && item.details.length > 0 && (
                      <ul className="assessment-card-details">
                        {item.details.map((detail, dIdx) => (
                          <li key={dIdx} className="assessment-detail-item">
                            <span className="assessment-detail-bullet" aria-hidden="true">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </span>
                            <span className="assessment-detail-text">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {/* Assessment Methodology */}
          {Array.isArray(assessmentSystem.methodology) && assessmentSystem.methodology.length > 0 && (
            <div className="assessment-methodology edu-animate">
              <div className="methodology-header">
                <div className="methodology-badge-wrap">
                  <span className="methodology-badge">Continuous Cycle</span>
                </div>
                <h4 className="methodology-title">{assessmentSystem.assessment_methodology_title}</h4>
                <p className="methodology-subtitle">{assessmentSystem.assessment_methodology_subtitle}</p>
              </div>
              <div className="methodology-grid">
                {assessmentSystem.methodology.map((item, index) => {
                  const stepNum = String(index + 1).padStart(2, '0');
                  return (
                    <div className="methodology-card" key={item.phase || index} style={{ animationDelay: `${index * 90}ms` }}>
                      <div className="methodology-number">{stepNum}</div>
                      <h5 className="methodology-phase">{item.phase}</h5>
                      <p className="methodology-desc">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Benefits / What This Means for Your Child */}
          {Array.isArray(assessmentSystem.what_this_means) && assessmentSystem.what_this_means.length > 0 && (
            <div className="assessment-benefits edu-animate">
              <div className="benefits-header">
                <div className="benefits-badge-wrap">
                  <span className="benefits-badge">Student-Centric Value</span>
                </div>
                <h4 className="benefits-title">What This Means for Your Child</h4>
                <p className="benefits-subtitle">
                  How our multi-dimensional assessment approach translates into real confidence, daily growth, and lifelong capability.
                </p>
              </div>
              <div className="benefits-grid">
                {assessmentSystem.what_this_means.map((benefit, bIdx) => (
                  <div className="benefit-item" key={benefit.title || bIdx} style={{ animationDelay: `${bIdx * 100}ms` }}>
                    <div className="benefit-icon" aria-hidden="true">
                      {getAssessmentIcon(benefit.icon || ['book', 'chart', 'users', 'zap'][bIdx % 4])}
                    </div>
                    <h5 className="benefit-title">{benefit.title}</h5>
                    <p className="benefit-desc">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assessment Calendar / Timeline */}
          {Array.isArray(assessmentSystem.assessment_calendar) && assessmentSystem.assessment_calendar.length > 0 && (
            <div className="assessment-timeline edu-animate">
              <div className="timeline-header">
                <div className="timeline-badge-wrap">
                  <span className="timeline-badge">Academic Milestones</span>
                </div>
                <h4 className="timeline-title">{assessmentSystem.assessment_calendar_header_title}</h4>
                <p className="timeline-subtitle">{assessmentSystem.assessment_calendar_header_subtitle}</p>
              </div>
              <div className="timeline-cards">
                {assessmentSystem.assessment_calendar.map((item, tIdx) => {
                  const calendarIcons = ['calendar', 'clipboard', 'chart', 'trending', 'award'];
                  const iconName = item.icon || calendarIcons[tIdx % calendarIcons.length];
                  const hasActivities = Array.isArray(item.activities) && item.activities.length > 0;
                  return (
                    <div className="timeline-card" key={item.period || item.title || tIdx} style={{ animationDelay: `${tIdx * 90}ms` }}>
                      <div className="timeline-card-header">
                        <div className="timeline-card-icon" aria-hidden="true">
                          {getAssessmentIcon(iconName)}
                        </div>
                        <span className="timeline-card-period">{item.period || `Stage ${tIdx + 1}`}</span>
                      </div>
                      <h5 className="timeline-card-title">{item.title || item.period}</h5>
                      {item.description && (
                        <p className="timeline-card-desc">{item.description}</p>
                      )}
                      {hasActivities && (
                        <ul className="timeline-card-activities">
                          {item.activities.map((act, aIdx) => (
                            <li key={aIdx} className="timeline-activity-item">
                              <span className="timeline-activity-dot" aria-hidden="true" />
                              <span>{act}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Assessment CTA */}
          <div className="assessment-cta edu-animate">
            <div className="cta-content">
              <div className="cta-badge-wrap">
                <span className="cta-badge">Consultation &amp; Advisory</span>
              </div>
              <h4 className="cta-title">Questions About Assessment?</h4>
              <p className="cta-desc">
                We are happy to discuss how we evaluate student progress, support individual growth, and collaborate with parents for academic excellence.
              </p>
              <button
                className="assessment-cta-button"
                type="button"
                onClick={() => onNavigate && onNavigate('contact-us', 'contact-form')}
              >
                Schedule a Conversation
                <span className="cta-button-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="special-programs" id="special-programs">
        {/* Decorative Background Elements */}
        <div className="special-programs-decor" aria-hidden="true">
          <div className="decor-glow-left" />
          <div className="decor-glow-right" />
        </div>

        <div className="special-programs-shell">
          {/* Header */}
          <div className="special-programs-header">
            <div className="special-programs-kicker-wrap">
              <span className="special-programs-kicker-dot" />
              <span className="special-programs-kicker">{specialProgramsShell.kicker}</span>
            </div>
            
            <h3 className="special-programs-title">
              {specialProgramsShell.title}
            </h3>
            
            <p className="special-programs-subtitle">
              {specialProgramsShell.subtitle}
            </p>
            
            <p className="special-programs-desc">
              {specialProgramsShell.description}
            </p>

            <div className="special-programs-actions">
              <div className="special-programs-note-wrap">
                <span className="note-pulse" />
                <p className="special-programs-note">
                  {specialProgramsShell.note}
                </p>
              </div>
              <button
                type="button"
                className="special-programs-link-btn"
                onClick={() => onNavigate && onNavigate('student-life', 'programs')}
              >
                <span>Explore Student Life Hub</span>
                <span className="link-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </div>

          {/* Quick Metrics / Stats Bar */}
          <div className="special-programs-stats edu-animate">
            {specialProgramsShell.stats.map((stat, idx) => (
              <div className="special-programs-stat" key={stat.label || idx}>
                <div className="stat-icon-wrap">
                  {idx === 0 ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
                    </svg>
                  ) : idx === 1 ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  )}
                </div>
                <div className="stat-content">
                  <span className="special-programs-stat-value">{stat.value}</span>
                  <span className="special-programs-stat-label">{stat.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Programs Grid */}
          <div className="special-programs-grid">
            {specialPrograms.map((item, idx) => {
              const meta = getSpecialProgramMeta(item.title, idx);
              return (
                <article className="special-programs-card edu-animate" key={item.title || idx}>
                  <div className="special-programs-media">
                    <img
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = getSpecialProgramFallback(item.title, idx);
                      }}
                    />
                    <div className="special-programs-media-overlay" />
                    <div className="special-programs-card-badge">
                      <span className="badge-code">{meta.code}</span>
                      <span className="badge-divider">•</span>
                      <span className="badge-category">{meta.category}</span>
                    </div>
                  </div>
                  
                  <div className="special-programs-body">
                    <div className="special-programs-card-tag">
                      <span className="tag-dot" />
                      <span className="tag-text">{meta.badge}</span>
                    </div>
                    
                    <h4 className="special-programs-card-title">{item.title}</h4>
                    <p className="special-programs-card-desc">{item.description}</p>
                    
                    <div className="special-programs-card-footer">
                      <div className="special-programs-benefit">
                        <span className="benefit-label">Key Focus</span>
                        <span className="benefit-text">{meta.benefit}</span>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </section>
  );
}

export default EducationProgramPage;
